import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const CASE_TYPES = [
  "Personal Injury / Accident",
  "Wrongful Death",
  "Employment / Wage Dispute",
  "Wrongful Termination",
  "Workplace Discrimination / Harassment",
  "Civil Rights Violation",
  "Other",
];

const initial = { name: "", phone: "", email: "", case_type: CASE_TYPES[0], preferred_date: "", preferred_time: "", message: "" };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-semibold tracking-wider text-gold/80 uppercase">{label}</span>
    {children}
  </label>
);

const inputCls = "w-full rounded-lg border border-gold/20 bg-navy-950/80 px-4 py-2.5 text-sm text-slate-100 placeholder-slate-500 outline-none transition-all focus:border-gold focus:ring-2 focus:ring-gold/30";

export const BookingModal = ({ open, onClose }) => {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await api.post("/enquiries", { ...form, type: "consultation" });
      setStatus({ state: "success", msg: "" });
      setForm(initial);
    } catch (err) {
      setStatus({ state: "error", msg: formatApiError(err) });
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] flex items-end justify-center bg-navy-950/80 backdrop-blur-sm sm:items-center sm:p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", damping: 28, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-gold/25 bg-navy-900 p-7 shadow-2xl shadow-black/60 sm:rounded-2xl"
          >
            <div className="mb-6 flex items-start justify-between">
              <div>
                <p className="font-mono text-[11px] tracking-[0.3em] text-gold/80 uppercase">No Fee Unless We Win</p>
                <h3 className="mt-1 font-display text-2xl font-bold text-slate-50">Book a Free Consultation</h3>
              </div>
              <button data-testid="booking-modal-close-btn" onClick={onClose} aria-label="Close" className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-navy-800 hover:text-gold">
                <X size={20} />
              </button>
            </div>

            {status.state === "success" ? (
              <div data-testid="booking-success-message" className="flex flex-col items-center py-10 text-center">
                <CheckCircle2 size={52} className="mb-4 text-gold" />
                <h4 className="font-display text-xl font-semibold text-slate-50">Consultation Requested</h4>
                <p className="mt-2 max-w-xs text-sm text-slate-400">
                  Thank you. Our team will confirm your consultation shortly. For urgent matters call (417) 283-1384.
                </p>
                <button onClick={onClose} className="mt-6 rounded-full border border-gold/40 px-6 py-2.5 text-sm font-semibold text-gold hover:bg-gold/10">Close</button>
              </div>
            ) : (
              <form data-testid="booking-modal-form" onSubmit={submit} className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Full Name *">
                    <input data-testid="booking-form-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Jane Doe" />
                  </Field>
                  <Field label="Phone *">
                    <input data-testid="booking-form-phone-input" required type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(555) 123-4567" />
                  </Field>
                </div>
                <Field label="Email">
                  <input data-testid="booking-form-email-input" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" />
                </Field>
                <Field label="Case Type">
                  <select data-testid="booking-form-case-type-select" value={form.case_type} onChange={set("case_type")} className={inputCls}>
                    {CASE_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Preferred Date">
                    <input data-testid="booking-form-preferred-date" type="date" value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} />
                  </Field>
                  <Field label="Preferred Time">
                    <input data-testid="booking-form-preferred-time" type="time" value={form.preferred_time} onChange={set("preferred_time")} className={inputCls} />
                  </Field>
                </div>
                <Field label="Brief Case Summary">
                  <textarea data-testid="booking-form-message-input" rows={3} value={form.message} onChange={set("message")} className={inputCls} placeholder="Tell us briefly what happened..." />
                </Field>

                {status.state === "error" && (
                  <p data-testid="booking-form-error" className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{status.msg}</p>
                )}

                <button
                  data-testid="booking-form-submit-btn"
                  type="submit"
                  disabled={status.state === "loading"}
                  className="btn-sheen w-full rounded-full bg-gradient-to-r from-[#D4AF37] via-[#C5A059] to-[#B38F32] py-3.5 text-sm font-bold text-navy-950 shadow-lg shadow-gold/25 transition-all hover:from-[#E6CA65] active:scale-[0.98] disabled:opacity-60"
                >
                  {status.state === "loading" ? "Submitting..." : "Request Free Consultation"}
                </button>
                <p className="text-center text-[11px] text-slate-500">Confidential. Submitting does not create an attorney-client relationship.</p>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
